/**
 * reportUtils.js — Pure helper functions for the Reports feature.
 * No React dependency — safe to import anywhere.
 */

export const getUserDatetime = () =>
  new Date().toLocaleString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZoneName: "short",
  });

/**
 * Escape `[N]` citation references to `@@CITE:N@@` tokens so
 * markdown doesn't treat them as link references.
 * Skips content inside fenced code blocks to avoid mangling
 * mermaid diagrams and other code.
 */
export const escapeCitations = (text) => {
  const parts = text.split(/(```[\s\S]*?```)/g);
  return parts
    .map((part, i) =>
      i % 2 === 0 ? part.replace(/\[(\d+)\]/g, "@@CITE:$1@@") : part,
    )
    .join("");
};

/**
 * Close unclosed markdown fences, backticks, bold, and italic.
 * Used during streaming when content is often in a broken state.
 */
export const healMarkdown = (text) => {
  let s = text;
  const fenceCount = (s.match(/```/g) || []).length;
  if (fenceCount % 2 !== 0) s += "\n```";
  const withoutFences = s.replace(/```[\s\S]*?```/g, "");
  if ((withoutFences.match(/`/g) || []).length % 2 !== 0) s += "`";
  const lastLine = s.split("\n").pop() || "";
  if ((lastLine.match(/\*\*/g) || []).length % 2 !== 0) s += "**";
  const singleStars = lastLine.replace(/\*\*/g, "").match(/\*/g) || [];
  if (singleStars.length % 2 !== 0) s += "*";
  return s;
};

/**
 * Strip the `[N] ...` citation footer from markdown text.
 * We render it separately as a styled CitationFooter component.
 */
export const stripCitationFooter = (text) => {
  if (!text) return text;
  const lines = text.split("\n");
  let citationStart = null;
  for (let i = lines.length - 1; i >= 0; i--) {
    const stripped = lines[i].trim();
    if (/^\[\d+\]/.test(stripped)) citationStart = i;
    else if (stripped === "") continue;
    else if (citationStart !== null) break;
  }
  if (citationStart !== null)
    return lines.slice(0, citationStart).join("\n").trimEnd();
  return text;
};

/** Regex for annotation comments: `<!-- @annotation: ... -->` */
export const ANNOTATION_RE = /<!--\s*@annotation:\s*(.*?)\s*-->/g;

/**
 * Extract annotations with their character offsets from content.
 * Used by ReportDocument to render margin notes.
 */
export const extractAnnotations = (text) => {
  const annotations = [];
  let match;
  const re = /<!--\s*@annotation:\s*(.*?)\s*-->/g;
  while ((match = re.exec(text)) !== null) {
    annotations.push({ offset: match.index, text: match[1] });
  }
  return annotations;
};
